import { useCallback, useRef } from 'react';
import { useDidMount } from '@/hooks/lifecycle/useDidMount';
import { useBoolState } from '@/hooks/state/useBoolState';
// eslint-disable-next-line no-irregular-whitespace
const kanaExtractionPattern = /[^ 　ぁあ-んー]/g;
const kanaCompactingPattern = /[ぁぃぅぇぉっゃゅょ]/g;
const initializeValues = {
    baseKana: '',
    furigana: '',
    ignoreString: '',
    input: '',
    isConverting: false,
    values: [],
};
/**
 * ふりがな自動入力 ふりがな自動入力
 */
export const useAutoKana = (options) => {
    const { checkInterval = 30, targetRef } = options;
    const timerRef = useRef(null);
    // 入力関連の値
    const valuesRef = useRef(initializeValues);
    const { boolState: isActive, changeFalse: stop, changeTrue: start, } = useBoolState({ initialState: true });
    /**
     * 自動入力の値登録
     */
    const setFurigana = useCallback((newValues) => {
        if (valuesRef.current.isConverting)
            return;
        if (newValues) {
            valuesRef.current.values = newValues;
        }
        if (isActive) {
            valuesRef.current.furigana =
                valuesRef.current.baseKana + valuesRef.current.values.join('');
        }
    }, [isActive, valuesRef]);
    /**
     * 削除対象の文字列削除
     * @param newInput
     * @returns {string}
     */
    const removeString = useCallback((newInput) => {
        if (newInput.indexOf(valuesRef.current.ignoreString) !== -1) {
            return String(newInput).replace(valuesRef.current.ignoreString, '');
        }
        const ignoreArray = valuesRef.current.ignoreString.split('');
        const inputArray = newInput.split('');
        for (let i = 0; i < ignoreArray.length; i += 1) {
            if (ignoreArray[i] === inputArray[i]) {
                inputArray[i] = '';
            }
        }
        return inputArray.join('');
    }, [valuesRef]);
    /**
     * 変換
     */
    const onConvert = useCallback(() => {
        valuesRef.current = Object.assign(Object.assign({}, valuesRef.current), { baseKana: valuesRef.current.baseKana + valuesRef.current.values.join(''), isConverting: true, values: [] });
    }, [valuesRef]);
    /**
     * 変換判定
     * @param newValues
     */
    const checkConvert = useCallback((newValues) => {
        if (valuesRef.current.isConverting)
            return;
        if (Math.abs(valuesRef.current.values.length - newValues.length) > 1) {
            const tmpValues = newValues
                .join('')
                .replace(kanaCompactingPattern, '')
                .split('');
            if (Math.abs(valuesRef.current.values.length - tmpValues.length) > 1) {
                onConvert();
            }
        }
        else if (valuesRef.current.values.length === valuesRef.current.input.length &&
            valuesRef.current.values.join('') !== valuesRef.current.input) {
            if (valuesRef.current.input.match(kanaExtractionPattern)) {
                onConvert();
            }
        }
    }, [onConvert, valuesRef]);
    /**
     * 入力値のチェック
     */
    const checkValue = useCallback(() => {
        const inputElement = targetRef === null || targetRef === void 0 ? void 0 : targetRef.current;
        let newInput = '';
        newInput = inputElement ? inputElement.value : '';
        if (newInput === '') {
            valuesRef.current = initializeValues;
            setFurigana();
        }
        else {
            newInput = removeString(newInput);
            if (valuesRef.current.input === newInput)
                return; // no changes
            valuesRef.current = Object.assign(Object.assign({}, valuesRef.current), { input: newInput });
            if (valuesRef.current.isConverting)
                return;
            const newValues = newInput.replace(kanaExtractionPattern, '').split('');
            checkConvert(newValues);
            setFurigana(newValues);
        }
    }, [checkConvert, setFurigana, removeString, targetRef, valuesRef]);
    /**
     * setInterval(入力値チェック)
     */
    const customSetInterval = useCallback(() => {
        timerRef.current = setInterval(checkValue, checkInterval);
    }, [checkInterval, checkValue, timerRef]);
    /**
     * clearInterval(入力値チェック)
     */
    const customClearInterval = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
    }, [timerRef]);
    /**
     * 入力時のイベント
     */
    const onInput = useCallback(() => {
        const inputElement = targetRef.current;
        if (!inputElement) {
            return;
        }
        valuesRef.current = Object.assign(Object.assign({}, valuesRef.current), { ignoreString: inputElement.value || '', isConverting: false });
    }, [targetRef, valuesRef]);
    /**
     * inputElement blur イベント
     */
    const onBlur = useCallback(() => {
        customClearInterval();
    }, [customClearInterval]);
    /**
     * inputElement focus イベント
     */
    const onFocus = useCallback(() => {
        onInput();
        customSetInterval();
    }, [customSetInterval, onInput]);
    /**
     * inputElement onKeyDown イベント
     */
    const onKeyDown = useCallback(() => {
        if (valuesRef.current.isConverting) {
            onInput();
        }
    }, [onInput, valuesRef]);
    /**
     * inputElement イベント登録(blur, focus, keydown)
     */
    const registerEvents = useCallback((inputElement) => {
        inputElement.addEventListener('blur', onBlur);
        inputElement.addEventListener('focus', onFocus);
        inputElement.addEventListener('keydown', onKeyDown);
    }, [onBlur, onFocus, onKeyDown]);
    /**
     * inputElement イベント解除(blur, focus, keydown)
     */
    const removeEvents = useCallback((inputElement) => {
        inputElement.removeEventListener('blur', onBlur);
        inputElement.removeEventListener('focus', onFocus);
        inputElement.removeEventListener('keydown', onKeyDown);
    }, [onBlur, onFocus, onKeyDown]);
    /**
     * ふりがなの値取得
     */
    const getFurigana = useCallback(() => valuesRef.current.furigana, [valuesRef]);
    // input イベント登録
    useDidMount(() => {
        const inputElement = targetRef.current;
        // 対象がない場合イベントを登録しない
        if (!inputElement) {
            throw new Error('Element not found');
        }
        registerEvents(inputElement);
        return () => {
            removeEvents(inputElement);
        };
    });
    return {
        getFurigana,
        start,
        stop,
    };
};
